/**
 * Der Rundgang nach den Fragen, seit 29.09.2026 als eine Geschichte.
 *
 * ANLASS (Robert, 29.09.2026): Die vier Bildschirme vom 28.09. waren gut,
 * aber „zu wenig Animation und entertaining und keine durchgehende Story, die
 * man gerne liest, es überfordert noch“. Jeder Bildschirm hatte vier bis
 * sechs Karten. Entwurf „Onboarding Story“ im Claude-Design-Projekt, von
 * Robert so abgenommen.
 *
 * JETZT: eine Headline („Alle Lieferanten im Blick.“, dasselbe Beispiel wie
 * die Szenen auf /guide) geht durch vier Szenen, je ein Gedanke:
 *
 *   0. Danke, und die vier Kapitel als Pfad
 *   1. Du schreibst in Claude, wie immer (die Zeile mit dem Werkzeugnamen ist das Erkennen)
 *   2. 99takes schreibt Fassungen (Methoden, Platzhalter statt erfundener Zahl)
 *   3. Deine Zielgruppe liest mit (Personas, Kernzielgruppe und erweiterte Zielgruppe)
 *   4. Am Ende steht ein Urteil (und Ergebnis melden)
 *   5. Jetzt du: mit Claude verbinden, vier Schritte (seit 12.10.2026; die
 *      Freigabe der Werkzeuge als Schritt 3 seit 16.10.2026)
 *
 * „Außerdem", „Was die Simulation sieht" und „Wenn nichts passiert" stehen
 * weiter auf /guide (`Erkennen` unten wird dort gebraucht).
 *
 * BEWEGUNG: Jede Szene hat eine Uhr (`useUhr`). Elemente blenden ein, wenn
 * die Uhr ihre Zeit erreicht; das Segment oben fuellt sich im selben Takt.
 * Nichts springt von allein weiter, „Weiter" ruft nur kurz, wenn die Szene
 * fertig ist. Bei prefers-reduced-motion steht die Uhr auf Unendlich: der
 * Endzustand steht sofort da. Aller Text ist immer im Dokument, die
 * Einblendung ist nur Deckkraft, damit Screenreader die ganze Szene lesen.
 *
 * KEINE ERFUNDENEN ZAHLEN: Preise aus preise.ts, der Werkzeugtitel aus
 * anzeige.ts, das Guthaben aus billing/status (die Startgutschrift ist ein
 * Secret, keine Konstante). Die Szenen sind gezeichnet und tragen erfundene
 * Demodaten, und das steht dabei. Der Satz zum Start ist eine echte
 * Anweisung an Claude (erster-lauf.ts).
 *
 * Kein Zustand in der Datenbank: wer die Fragen einmal hinter sich hat, sieht
 * den Rundgang nicht wieder; auf /guide steht das meiste zum Nachschlagen.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { CLAUDE_CONNECTORS, connectorPfad, fensterHinweis, freigabeHinweis, MCP_URL } from '../config';
import { CHALLENGER, EINRICHTUNG, STANDARD, VERGLEICH_STANDARD } from '../preise';
import { ANZEIGE } from '../../../../supabase/functions/_shared/sim/anzeige.ts';
import { Kopierzeile, Zeichen } from '../ui';
import { locale, sprache, t } from '../sprache';

/** Die vier Kapitel, wie sie auf dem Danke-Bildschirm stehen. */
export const KAPITEL = ['Du fragst', '99takes schreibt', 'Personas lesen', 'Du entscheidest'] as const;
export const KAPITEL_EN = ['You ask', '99takes writes', 'Personas read', 'You decide'] as const;

/** Der Satz, mit dem man in Claude prueft, ob 99takes verbunden ist. get_balance antwortet mit dem Guthaben des Kontos. */
export const TESTSATZ = 'Bist du mit 99takes verbunden? Wie viel Guthaben habe ich?';
export const TESTSATZ_EN = 'Are you connected to 99takes? What is my balance?';
/** Der Test-Satz in der Sprache der App (beim Rendern aufrufen). */
export const testsatz = (): string => t(TESTSATZ, TESTSATZ_EN);

const KNOPF = 'inline-flex h-[2.75rem] cursor-pointer items-center justify-center gap-1.5 rounded-lg border-0 bg-knopf px-[1.375rem] text-[0.9375rem] font-semibold text-auf-marke '
  + 'shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(20_18_12/0.18)] transition-[background-color,transform] duration-150 hover:bg-knopf-hover active:translate-y-px';

const LEISE = 'cursor-pointer rounded-md border-0 bg-transparent px-2 py-1.5 text-sm text-text-leise transition-colors duration-150 hover:bg-ebene hover:text-tinte';

const bewegungAus = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Vergangene Zeit seit Beginn der Szene in Millisekunden. Laeuft bis `ende`
 * und bleibt dort stehen. Neuer `schluessel` heisst: von vorn. Bei
 * reduzierter Bewegung Unendlich, also der Endzustand.
 */
export function useUhr(schluessel: unknown, ende: number) {
  const [zeit, setZeit] = useState(() => (bewegungAus() ? Infinity : 0));
  useEffect(() => {
    if (bewegungAus()) { setZeit(Infinity); return; }
    setZeit(0);
    let start: number | null = null;
    let id = 0;
    const tick = (jetzt: number) => {
      start ??= jetzt;
      const z = jetzt - start;
      setZeit(z >= ende ? Infinity : z);
      if (z < ende) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [schluessel, ende]);
  return zeit;
}

/** Blendet ein, sobald die Uhr `ab` erreicht. Der Inhalt steht von Anfang an im Dokument. */
function Rein({ ab, zeit, className = '', style, children }: {
  ab: number; zeit: number; className?: string; style?: CSSProperties; children: ReactNode;
}) {
  return <div className={`rein ${zeit >= ab ? 'da' : ''} ${className}`} style={style}>{children}</div>;
}

const Zitat = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <p className={`m-0 font-serif text-[1rem] leading-snug text-text italic ${className}`}>{children}</p>
);

const Etikett = ({ children, gut = false }: { children: ReactNode; gut?: boolean }) => (
  <span className={`text-[0.65625rem] font-bold tracking-[0.08em] uppercase ${gut ? 'text-gut' : 'text-text-hell'}`}>{children}</span>
);

const Preis = ({ children, frei = false }: { children: ReactNode; frei?: boolean }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-[0.78125rem] font-semibold ${frei ? 'bg-gut/10 text-gut' : 'bg-marke/[0.09] text-marke-tief'}`}>
    {children}
  </span>
);

// ─── Die vier Szenen ────────────────────────────────────────────────────────
//
// Zeiten in Millisekunden ab Beginn der Szene. `ende` ist, wann „Weiter" ruft
// und das Segment voll ist.

const TIPP_MS = 22;
// Seit 30.09.2026 der A/B-Test per Link: der Weg, den 99takes in Claude zuerst führt.
export const BEISPIEL_PROMPT = 'Mach einen A/B-Test für https://lieferantenblick.de';
export const BEISPIEL_PROMPT_EN = 'Run an A/B test for https://lieferantenblick.de';
const S1 = { leuchten: 150, tippen: 650, gesendet: 2450, werkzeug: 3100, fertig: 4300, pfeil: 4700 };

function SzeneChat({ zeit }: { zeit: number }) {
  const prompt = t(BEISPIEL_PROMPT, BEISPIEL_PROMPT_EN);
  const gesendet = zeit >= S1.gesendet;
  const zeichen = gesendet ? 0 : Math.max(0, Math.min(prompt.length, Math.floor((zeit - S1.tippen) / TIPP_MS)));
  const leuchtet = zeit >= S1.leuchten && !gesendet;
  return (
    <div className="flex min-h-[15rem] flex-col overflow-hidden sm:min-h-[18.5rem] rounded-[0.625rem] border border-linie-zart bg-ebene">
      <div className="flex items-center gap-1.5 border-b border-linie-zart px-2.5 py-2 text-[0.6875rem] text-text-hell" aria-hidden>
        <i className="size-2 rounded-full bg-linie" /><i className="size-2 rounded-full bg-linie" /><i className="size-2 rounded-full bg-linie" />
        <span className="ml-1.5">{t('Dein Chat mit Claude, gezeichnet', 'Your chat with Claude, illustrated')}</span>
      </div>
      <div className="flex flex-1 flex-col justify-end gap-2.5 p-3.5">
        <Rein ab={S1.gesendet + 50} zeit={zeit} className="max-w-[85%] self-end rounded-[14px_14px_4px_14px] bg-blase px-3 py-2 text-[0.875rem] text-tinte">
          <span className="sr-only">{t('Du fragst: ', 'You ask: ')}</span>{prompt}
        </Rein>
        <Rein ab={S1.werkzeug} zeit={zeit} className="flex flex-wrap items-center gap-2 rounded-[0.5625rem] border border-linie bg-flaeche px-2.5 py-2 text-[0.8125rem]">
          <span className="neun-ring grid shrink-0 place-items-center rounded-[5px]"><Zeichen klein className="size-[1.125rem]" /></span>
          <b className="font-semibold text-tinte">{ANZEIGE.test_page.title}</b>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[0.78125rem] font-semibold text-marke-tief">
            {zeit < S1.fertig && <span aria-hidden className="size-3 animate-spin rounded-full border-2 border-linie border-t-marke" />}
            <span className={`rein ${zeit >= S1.fertig ? 'da' : ''}`}>{t('Preis je Umfang', 'Price by scope')}</span>
          </span>
        </Rein>
        <Rein ab={S1.pfeil} zeit={zeit} className="pl-1 text-[0.78125rem] font-semibold text-marke-tief">
          <span aria-hidden>↑ </span>{t('Das ist 99takes. Vorher nennt Claude den Preis.', 'That is 99takes. Claude names the price first.')}
        </Rein>
      </div>
      <div aria-hidden className={`mx-2.5 mb-2.5 flex min-h-[2.75rem] items-center gap-1.5 rounded-[0.625rem] border bg-flaeche py-2 pr-2.5 pl-3 text-[0.875rem] text-tinte transition-[border-color,box-shadow] duration-300 ${
        leuchtet ? 'border-marke shadow-[0_0_0_4px_rgb(63_138_135/0.16)]' : 'border-linie'}`}>
        <span className="min-w-0 flex-1">
          {zeichen > 0 ? prompt.slice(0, zeichen) : <span className="text-text-hell">{leuchtet ? t('Du tippst …', 'You type …') : t('Schreib Claude eine Nachricht', 'Write Claude a message')}</span>}
        </span>
        <span className={`grid size-[1.625rem] shrink-0 place-items-center rounded-[7px] text-[0.875rem] font-bold text-white transition-colors duration-200 ${leuchtet ? 'bg-marke' : 'bg-linie'}`}>↑</span>
      </div>
    </div>
  );
}

const FASSUNGEN: Array<{ methode: string; text: ReactNode; ab: number; en: { methode: string; text: ReactNode } }> = [
  { methode: 'Bisher, ab jetzt', text: 'Standorte kommen dazu, der Preis nicht. Festpreis je Standort, ohne Einrichtungsprojekt.', ab: 700,
    en: { methode: 'Before, from now on', text: "Sites get added, the price doesn't. Fixed price per site, no setup project." } },
  { methode: 'Beleg zuerst', text: null, ab: 1100, en: { methode: 'Proof first', text: null } },
  { methode: 'Nutzengetrieben', text: 'Der nächste Standort kostet, was der erste gekostet hat.', ab: 1500,
    en: { methode: 'Benefit-driven', text: 'The next site costs what the first one cost.' } },
];
const S2 = { marker: 2300, notiz: 2800 };

function SzeneFassungen({ zeit }: { zeit: number }) {
  return (
    <>
      <Rein ab={0} zeit={zeit} className="rounded-[0.625rem] border border-dashed border-linie bg-ebene px-3 py-2.5">
        <Etikett>{t('Heute', 'Today')}</Etikett>
        <Zitat className="text-[1.0625rem] text-text-hell">{t('Alle Lieferanten im Blick.', 'All suppliers in view.')}</Zitat>
      </Rein>
      <ol className="m-0 mt-3 grid list-none gap-2.5 p-0">
        {FASSUNGEN.map((f, i) => (
          <li key={f.methode}>
            <Rein ab={f.ab} zeit={zeit} className="von-oben rounded-[0.625rem] border border-linie bg-flaeche px-3 py-2.5 shadow-[0_6px_18px_-14px_rgb(20_18_12/0.4)]">
              <div className="mb-1 flex items-center gap-2">
                <span className="font-serif text-[1.0625rem] leading-none font-semibold text-marke-tief">{i + 1}</span>
                <span className="rounded-full bg-marke/[0.09] px-2 py-0.5 text-[0.71875rem] font-semibold text-marke-tief">{t(f.methode, f.en.methode)}</span>
              </div>
              <Zitat>
                {t(f.text, f.en.text) ?? t(
                  <>Festpreis je Standort, Laufzeit <mark className={`marker rounded-[3px] px-0.5 text-inherit ${zeit >= S2.marker ? 'da' : ''}`}>[BELEG: Mindestlaufzeit]</mark>. Ohne Einrichtungsprojekt.</>,
                  <>Fixed price per site, term <mark className={`marker rounded-[3px] px-0.5 text-inherit ${zeit >= S2.marker ? 'da' : ''}`}>[BELEG: minimum term]</mark>. No setup project.</>,
                )}
              </Zitat>
            </Rein>
          </li>
        ))}
      </ol>
      <Rein ab={S2.notiz} zeit={zeit} className="mt-3 flex items-start gap-2 text-[0.8125rem] text-text-leise">
        <span aria-hidden className="mt-1.5 size-2.5 shrink-0 rounded-[3px] bg-[rgb(234_179_8/0.6)]" />
        {t('Den Platzhalter füllst du vor dem Run. Sonst liest die Jury die Klammer mit.', 'You fill in the placeholder before the run. Otherwise the jury reads the brackets too.')}
      </Rein>
    </>
  );
}

/** [Kürzel, Rolle, Satz, Rand, stimmt für die heutige Fassung, Rolle englisch, Satz englisch] */
const PERSONAS: Array<[string, string, string, boolean, boolean, string, string]> = [
  ['LE', 'Leitung Einkauf', 'Endlich steht der Preis oben.', false, false, 'Head of purchasing', 'Finally the price is at the top.'],
  ['IT', 'IT-Leitung', 'Ohne Einrichtungsprojekt? Das will ich sehen.', false, false, 'Head of IT', 'No setup project? I want to see that.'],
  ['CO', 'Controlling', 'Die Mindestlaufzeit fehlt neben dem Preis.', false, false, 'Financial controller', 'The minimum term is missing next to the price.'],
  ['GF', 'Geschäftsführung', 'Weniger Überraschungen, das zählt.', false, false, 'Managing director', 'Fewer surprises, that is what counts.'],
  ['WE', 'Werkstudent Einkauf', 'Für mich ändert der Preis oben nichts.', true, true, 'Purchasing intern', 'The price at the top changes nothing for me.'],
  ['AE', 'Assistenz Einkauf', 'Ich suche nur die Kontaktdaten.', true, false, 'Purchasing assistant', 'I am only looking for the contact details.'],
];
const S3 = { erste: 250, abstand: 420, stand: 3300 };

function SzeneJury({ zeit }: { zeit: number }) {
  return (
    <>
      <Rein ab={0} zeit={zeit} className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 text-[0.78125rem] text-text-hell">
        <span><b className="font-semibold text-tinte">{t('Einkauf Mittelstand', 'Mid-sized purchasing')}</b> · <span className="whitespace-nowrap">{t('12 Personas', '12 personas')}</span></span>
        <span className="whitespace-nowrap">{t('10 Kern, 2 erweitert', '10 core, 2 extended')}</span>
      </Rein>
      <ul className="m-0 grid list-none gap-[0.4375rem] p-0 lg:grid-cols-2">
        {PERSONAS.map(([kurz, rolleDe, satzDe, rand, heute, rolleEn, satzEn], i) => {
          const rolle = t(rolleDe, rolleEn);
          const satz = t(satzDe, satzEn);
          const ab = S3.erste + i * S3.abstand;
          // Auf dem Telefon nur der Kern: sechs Karten schieben „Weiter" weit
          // unter den Rand (Entwurf, 340 px). Die erweiterte Zielgruppe steht darunter als Satz.
          return (
            <li key={kurz} className={rand ? 'hidden sm:block' : ''}>
              <Rein ab={ab} zeit={zeit} className="grid h-full grid-cols-[1.875rem_minmax(0,1fr)_auto] items-start gap-2 rounded-[0.625rem] border border-linie-zart bg-flaeche px-2.5 py-2">
                <span aria-hidden className={`grid size-[1.875rem] place-items-center rounded-full text-[0.6875rem] font-bold ${rand ? 'bg-linie-zart text-text-hell' : 'bg-marke/[0.12] text-marke-tief'}`}>{kurz}</span>
                <div className="min-w-0">
                  <b className="text-[0.78125rem] font-semibold text-tinte">{rolle}</b>
                  {rand && <span title={t('erweiterte Zielgruppe', 'extended audience')} className="ml-1.5 rounded-full border border-linie px-1.5 text-[0.625rem] font-semibold whitespace-nowrap text-text-hell">{t('erweitert', 'extended')}</span>}
                  <div className={`rein ${zeit >= ab + 280 ? 'da' : ''}`}><Zitat className="mt-0.5 text-[0.84375rem]">{t(`„${satz}“`, `“${satz}”`)}</Zitat></div>
                </div>
                <span className={`rein rounded-full px-[0.4375rem] py-0.5 text-[0.65625rem] font-bold ${heute ? 'bg-linie-zart text-text-hell' : 'bg-gut/[0.12] text-gut'} ${zeit >= ab + 620 ? 'da' : ''}`}>
                  <span className="sr-only">{t('stimmt für ', 'votes for ')}</span>{heute ? t('Heute', 'Today') : t('Neu', 'New')}
                </span>
              </Rein>
            </li>
          );
        })}
      </ul>
      <p className="m-0 mt-2 text-[0.75rem] text-text-hell">{t(
        <>Dazu 6 weitere im Kern<span className="sm:hidden"> und 2 aus der erweiterten Zielgruppe, die nicht ins Urteil zählen</span>. Jede Persona liest unter 4 Jobs: 48 Einzelbewertungen.</>,
        <>Plus 6 more in the core<span className="sm:hidden"> and 2 from the extended audience, who don't count toward the verdict</span>. Each persona reads under 4 jobs: 48 individual ratings.</>,
      )}</p>
      <Rein ab={S3.stand} zeit={zeit} className="mt-3 flex items-baseline justify-center gap-2.5 rounded-[0.625rem] bg-gut/[0.08] p-2.5 text-[0.8125rem] text-gut">
        <span>{t('Im Kern', 'In the core')}</span><b className="font-serif text-[1.875rem] leading-none font-semibold">34 : 6</b><span>{t('für die neue Fassung', 'for the new version')}</span>
      </Rein>
    </>
  );
}

const S4 = { balken: 500, einwand: 1400, empfehlung: 2000, melden: 2700 };

function SzeneUrteil({ zeit }: { zeit: number }) {
  return (
    <>
      <Rein ab={100} zeit={zeit}>
        <Etikett gut>{t('Urteil', 'Verdict')}</Etikett>
        <p className="m-0 mt-1 mb-3.5 font-serif text-[1.5rem] leading-[1.15] font-semibold text-tinte">{t('Die neue Fassung gewinnt im Kern ', 'The new version wins in the core ')}<span className="whitespace-nowrap">34 : 6</span>.</p>
      </Rein>
      <Rein ab={S4.balken} zeit={zeit} className="grid gap-2">
        {([['Neu', '7,4', 74, 'bg-marke', 'New', '7.4'], ['Heute', '5,9', 59, 'bg-linie-stark', 'Today', '5.9']] as const).map(([nameDe, noteDe, w, farbe, nameEn, noteEn]) => {
          const note = t<string>(noteDe, noteEn);
          return (
          <div key={nameDe} className="grid grid-cols-[3.25rem_minmax(0,1fr)_2.125rem] items-center gap-2.5 text-[0.78125rem] text-text-leise">
            <span>{t<string>(nameDe, nameEn)}</span>
            <span aria-hidden className="balken-spur h-2.5 overflow-hidden rounded-full bg-linie-zart">
              <span className={`block h-full rounded-full ${farbe}`} style={{ '--w': `${w}%` } as CSSProperties} />
            </span>
            <b className="text-right text-tinte tabular-nums">{note}</b>
          </div>
          );
        })}
      </Rein>
      <Rein ab={S4.einwand} zeit={zeit} className="mt-3.5 rounded-[0.625rem] border border-linie-zart bg-ebene px-3 py-2.5">
        <Etikett>{t('Wichtigster Einwand', 'Main objection')}</Etikett>
        <Zitat>{t('„Die Mindestlaufzeit fehlt neben dem Preis.“', '“The minimum term is missing next to the price.”')}</Zitat>
        <span className="text-[0.75rem] text-text-hell">{t('Controlling', 'Financial controller')}</span>
      </Rein>
      <Rein ab={S4.empfehlung} zeit={zeit} className="mt-3.5 rounded-[0.5625rem] bg-ebene px-3 py-2 text-[0.875rem] text-text">
        <b className="font-semibold text-tinte">{t('Empfehlung:', 'Recommendation:')}</b>{t(' Laufzeit neben den Preis setzen, dann noch einmal prüfen.', ' Put the term next to the price, then test again.')}
      </Rein>
      <Rein ab={S4.melden} zeit={zeit} className="mt-3 flex items-start gap-2.5 text-[0.8125rem] text-text-leise">
        <span aria-hidden className="grid size-[1.375rem] shrink-0 place-items-center rounded-full bg-gut/[0.12] font-bold text-gut">↻</span>
        <span>{t('War die Fassung live? Melde Klicks oder Anfragen zurück. Daraus entsteht die Trefferquote: wie oft 99takes recht hatte.', 'Did the version go live? Report back clicks or inquiries. That builds the hit rate: how often 99takes was right.')}</span>
      </Rein>
    </>
  );
}

type SzeneText = { titel: string; text: string; preis: string };
export const SZENEN: Array<SzeneText & { en: SzeneText; frei: boolean; ende: number; Bild: (p: { zeit: number }) => ReactNode }> = [
  {
    titel: 'Du schreibst in Claude, wie immer.',
    text: '99takes hat kein eigenes Fenster. Es arbeitet in deinem Chat mit, jeder Schritt steht als eigene Zeile mit seinem Namen da.',
    preis: 'Verbinden kostet nichts',
    en: {
      titel: 'You write in Claude, as always.',
      text: '99takes has no window of its own. It works along in your chat, and each step shows up as its own line with its name.',
      preis: 'Connecting is free',
    },
    frei: true, ende: S1.pfeil + 700, Bild: SzeneChat,
  },
  {
    titel: '99takes schreibt neue Fassungen, jede nach einer anderen Methode.',
    text: 'Fehlt ein Beleg, steht ein Platzhalter im Text, eine Zahl erfindet 99takes nicht. Bei einer ganzen Seite siehst du die neue Fassung vor dem Test und gibst sie frei.',
    preis: `${CHALLENGER.von} bis ${CHALLENGER.bis} Credits, Preis vorher`,
    en: {
      titel: '99takes writes new versions, each with a different method.',
      text: 'If proof is missing, a placeholder goes in the text. 99takes does not invent numbers. For a whole page you see the new version before the test and approve it.',
      preis: `${CHALLENGER.von} to ${CHALLENGER.bis} credits, price shown first`,
    },
    frei: false, ende: S2.notiz + 700, Bild: SzeneFassungen,
  },
  {
    titel: 'Deine Zielgruppe liest mit.',
    text: 'Aus echten Kundenstimmen baut 99takes Personas. Sie lesen beide Fassungen, ohne zu wissen, welche neu ist. Ins Urteil zählt deine Kernzielgruppe, die erweiterte Zielgruppe siehst du daneben.',
    preis: `rund ${VERGLEICH_STANDARD} Credits mit ${STANDARD.personas} Personas`,
    en: {
      titel: 'Your audience reads along.',
      text: '99takes builds personas from real customer voices. They read both versions without knowing which one is new. Your core audience counts toward the verdict, and you see the extended audience alongside.',
      preis: `about ${VERGLEICH_STANDARD} credits with ${STANDARD.personas} personas`,
    },
    frei: false, ende: S3.stand + 700, Bild: SzeneJury,
  },
  {
    titel: 'Am Ende steht ein Urteil.',
    text: 'Du bekommst das Urteil in einem Satz, den wichtigsten Einwand und eine Empfehlung. Mehr zeigt Claude auf Nachfrage, und jeder Run steht danach unter „Runs“.',
    preis: 'Ergebnis melden ist kostenlos',
    en: {
      titel: 'At the end there is a verdict.',
      text: 'You get the verdict in one sentence, the main objection and a recommendation. Claude shows more on request, and every run then appears under “Runs”.',
      preis: 'Reporting the result is free',
    },
    frei: true, ende: S4.melden + 700, Bild: SzeneUrteil,
  },
];

/** Die Szene mit Titel, Text und Preis in der Sprache der App. */
const inSprache = <T extends SzeneText & { en: SzeneText }>(s: T): T => (sprache() === 'en' ? { ...s, ...s.en } : s);

// ─── Der Rundgang ───────────────────────────────────────────────────────────

/** 0 Danke, 1 bis 4 die Szenen, 5 „Jetzt bist du dran". */
const LETZTER = SZENEN.length + 1;

export function Rundgang({ ziel, weiter, zurueck, guthaben = null, uebersprungen = false }: {
  ziel: string;
  weiter: () => void;
  /** Zurueck aus dem ersten Bildschirm: zur letzten Frage. */
  zurueck: () => void;
  /** Credits auf dem Konto (billing/status). Ohne Zahl fehlt die Zeile. */
  guthaben?: number | null;
  /** Die Fragen wurden übersprungen: dann kein „Danke.“ zur Begrüßung. */
  uebersprungen?: boolean;
}) {
  const [schritt, setSchritt] = useState(0);
  const [richtung, setRichtung] = useState<'vor' | 'zurueck'>('vor');
  /** Zaehlt „Nochmal ansehen" hoch: neuer Schluessel, die Uhr beginnt von vorn. */
  const [runde, setRunde] = useState(0);
  const titel = useRef<HTMLHeadingElement>(null);
  // Wer aus claude.ai kommt, verbindet gerade schon (Zustimmung unter /connect).
  const freigabe = ziel.split(/[?#]/)[0] === '/connect';

  const szene = schritt >= 1 && schritt <= SZENEN.length ? inSprache(SZENEN[schritt - 1]!) : null;
  const ende = szene?.ende ?? (schritt === 0 ? 1400 : 1600);
  const zeit = useUhr(`${schritt}:${runde}`, ende);
  const fertig = zeit >= ende;

  // Beim Wechsel oben anfangen und den Titel vorlesen lassen.
  useEffect(() => {
    if (schritt === 0 && runde === 0) return;
    window.scrollTo?.({ top: 0 });
    titel.current?.focus({ preventScroll: true });
  }, [schritt]);

  const gehe = (n: number) => { setRichtung(n > schritt ? 'vor' : 'zurueck'); setSchritt(n); };
  const vor = () => (schritt < LETZTER ? gehe(schritt + 1) : weiter());
  const zurueckKnopf = () => (schritt === 0 ? zurueck() : gehe(schritt - 1));

  const weiterKnopf = (text: string) => (
    <button type="button" onClick={vor} autoFocus={schritt === 0} className={`${KNOPF} ${fertig ? 'knopf-ruft' : ''}`}>
      {text} <span aria-hidden>→</span>
    </button>
  );

  // Am Schluss: claude.ai in einem neuen Tab, hier geht es zum Ziel weiter
  // (/runs hakt „Mit Claude verbinden“ ab, sobald der erste Aufruf kommt).
  const schlussKnopf = freigabe ? weiterKnopf(t('Zur Freigabe', 'To approval')) : (
    <a href={CLAUDE_CONNECTORS} target="_blank" rel="noreferrer" onClick={weiter} className={`${KNOPF} no-underline ${fertig ? 'knopf-ruft' : ''}`}>
      {t('Claude öffnen', 'Open Claude')} <span aria-hidden>↗</span>
    </a>
  );
  const spaeter = !freigabe && (
    <button type="button" onClick={weiter} className={`whitespace-nowrap ${LEISE}`}>{t('Später', 'Later')}</button>
  );

  return (
    <div className="min-h-dvh">
      <header className="mx-auto flex max-w-[64rem] items-center justify-between gap-3 px-4 pt-5 sm:px-6">
        <button type="button" onClick={zurueckKnopf} className={`-ml-2 inline-flex items-center gap-1.5 ${LEISE}`}>
          <span aria-hidden>←</span> {t('Zurück', 'Back')}
        </button>
        {schritt === LETZTER ? (
          <span className="text-[0.75rem] text-text-hell">{t('Schritt 3 von 3', 'Step 3 of 3')}</span>
        ) : (
          <button
            type="button" onClick={weiter}
            className={`-mr-2 underline decoration-linie underline-offset-[3px] hover:decoration-text-hell ${LEISE}`}
          >
            {t('Überspringen', 'Skip')}
          </button>
        )}
      </header>

      <Fortschritt schritt={schritt} dauer={ende} runde={runde} />

      <main key={schritt} className={`${richtung === 'vor' ? 'szene-vor' : 'szene-zurueck'} mx-auto overflow-x-clip px-4 pt-8 sm:px-6 sm:pt-10 sm:pb-14 ${szene ? 'max-w-[64rem] pb-28' : schritt === LETZTER ? 'max-w-[36rem] pb-28' : 'max-w-[36rem] pb-14'}`}>
        {schritt === 0 && (
          <section className="mx-auto max-w-[34rem] pt-2 text-center">
            <div className="text-[0.75rem] font-semibold tracking-[0.07em] text-text-hell uppercase">{t('Bevor es losgeht', 'Before we start')}</div>
            <h1 ref={titel} tabIndex={-1} className="m-0 mt-2 font-serif text-[2.125rem] leading-[1.05] font-semibold tracking-[-0.02em] text-tinte outline-none sm:text-[2.75rem]">
              {uebersprungen ? t('So läuft es ab', 'How it works') : t('Danke.', 'Thanks.')}
            </h1>
            <p className="mx-auto mt-3 mb-9 max-w-[28ch] text-[1rem] leading-relaxed text-text-leise">
              {t('In einer Minute siehst du an einer Headline, wie 99takes in deinem Chat mit Claude arbeitet.', 'In one minute, one headline shows you how 99takes works in your chat with Claude.')}
            </p>
            <ol className="relative m-0 mb-10 grid list-none grid-cols-4 gap-2 p-0">
              <span aria-hidden className={`absolute top-[1.0625rem] right-[12.5%] left-[12.5%] h-0.5 origin-left bg-marke transition-transform delay-200 duration-[1100ms] ease-aus motion-reduce:transition-none ${zeit >= 80 ? 'scale-x-100' : 'scale-x-0'}`} />
              {t<readonly string[]>(KAPITEL, KAPITEL_EN).map((k, i) => (
                <li key={k}>
                  <Rein ab={250 + i * 260} zeit={zeit} className="relative flex flex-col items-center gap-2 text-[0.71875rem] leading-tight text-text-leise sm:text-[0.8125rem]">
                    <span className="grid size-9 place-items-center rounded-full border-2 border-marke bg-flaeche font-serif text-[1rem] font-semibold text-marke-tief">{i + 1}</span>
                    {k}
                  </Rein>
                </li>
              ))}
            </ol>
            {weiterKnopf(t('Los geht’s', 'Let’s go'))}
          </section>
        )}

        {szene && (
          // Der Satz steht unter dem Titel, auch am Telefon (Entwurf „Rundgang B“,
          // Canvas „99takes Onboarding Politur“, gewaehlt von Robert am
          // 12.10.2026). Vorher kam er nach dem Bild und lag bei 340 x 640
          // unter dem Rand: man sah ein Bild ohne Erklaerung.
          <section className="grid gap-[1.125rem] [grid-template-areas:'kt'_'st'_'kn'] lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-x-[3.25rem] lg:gap-y-0 lg:[grid-template-areas:'kt_st'_'kn_st']">
            <div className="[grid-area:kt] lg:pt-6">
              <div className="text-[0.75rem] font-semibold tracking-[0.07em] text-text-hell uppercase">{t(`Szene ${schritt} von ${SZENEN.length}`, `Scene ${schritt} of ${SZENEN.length}`)}</div>
              <h1 ref={titel} tabIndex={-1} className="m-0 mt-2 font-serif text-[1.6875rem] leading-[1.08] font-semibold tracking-[-0.018em] text-tinte outline-none sm:text-[2.25rem]">
                {szene.titel}
              </h1>
              <p className="m-0 mt-2 mb-3 max-w-[30rem] text-[0.9375rem] leading-relaxed text-text-leise sm:text-[1rem] lg:mt-3.5 lg:mb-4">{szene.text}</p>
              <Preis frei={szene.frei}>{szene.preis}</Preis>
            </div>
            <div className="[grid-area:st]">
              <div className="relative overflow-hidden rounded-[0.875rem] border border-linie bg-flaeche p-3 shadow-karte sm:p-[1.125rem]">
                <szene.Bild zeit={zeit} />
              </div>
              <p className="m-0 mt-2 text-right text-[0.6875rem] text-text-hell">{t('Beispiel mit erfundenen Daten', 'Example with made-up data')}</p>
            </div>
            <div className="[grid-area:kn]">
              <div className="hidden flex-wrap items-center gap-3 sm:flex lg:mt-7">
                {weiterKnopf(t('Weiter', 'Next'))}
                <button type="button" onClick={() => setRunde(r => r + 1)} className={`whitespace-nowrap ${LEISE}`}>
                  <span aria-hidden>↺ </span>{t('Nochmal ansehen', 'Watch again')}
                </button>
              </div>
            </div>
          </section>
        )}

        {schritt === LETZTER && (
          <JetztDu titel={titel} zeit={zeit} guthaben={guthaben} freigabe={freigabe} aktion={schlussKnopf} spaeter={spaeter} />
        )}
      </main>

      {/* Auf dem Telefon eine feste Leiste unten (Entwurf 2b, „Anleitung und
          Onboarding", gewaehlt von Robert am 04.10.2026): Bild und Text stehen
          untereinander, und „Weiter" lag bei 340 x 640 rund 400 px unter dem
          Bildschirmrand. Sie steht AUSSERHALB von <main>: die Szene gleitet
          per transform herein, und ein Vorfahr mit transform macht `fixed`
          zu „fest am Vorfahren". Bis zum 12.10.2026 stand sie darin und lag
          deshalb wieder unter dem Rand (gemessen: y = 782 bei 640 Höhe). */}
      {(szene || schritt === LETZTER) && (
        <div className="fixed inset-x-0 bottom-0 z-10 flex items-center gap-3 border-t border-linie bg-grund/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:hidden">
          {szene ? (
            <button type="button" onClick={() => setRunde(r => r + 1)} className={`whitespace-nowrap ${LEISE}`}>
              <span aria-hidden>↺ </span>{t('Nochmal', 'Again')}
            </button>
          ) : spaeter}
          <div className="flex-1 [&>*]:w-full">{szene ? weiterKnopf(t('Weiter', 'Next')) : schlussKnopf}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Ein Segment je Szene plus „Jetzt bist du dran". Das laufende fuellt sich im
 * Takt der Szene. Duenner als die Leiste der Fragen und mit Namen davor (seit
 * 12.10.2026): vorher sah sie aus wie die der Fragen und fing nach der
 * letzten Frage wieder leer an, als waere etwas verloren gegangen.
 */
function Fortschritt({ schritt, dauer, runde }: { schritt: number; dauer: number; runde: number }) {
  const n = SZENEN.length + 1;
  return (
    <div className="mx-auto mt-4 flex max-w-[64rem] items-center gap-2.5 px-4 sm:px-6">
      <span aria-hidden className="shrink-0 text-[0.71875rem] font-semibold tracking-[0.06em] text-marke-tief uppercase">{t('Rundgang', 'Tour')}</span>
      <ol aria-label={t('Rundgang', 'Tour')} className="m-0 flex flex-1 list-none gap-1 p-0">
        {Array.from({ length: n }, (_, i) => {
          const nr = i + 1;
          const laeuft = nr === schritt;
          return (
            <li key={i} className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-linie" aria-current={laeuft ? 'step' : undefined}>
              <span className="sr-only">{nr <= SZENEN.length ? t(`Szene ${nr}`, `Scene ${nr}`) : t('Jetzt bist du dran', 'Your turn')}{nr < schritt ? t(', erledigt', ', done') : ''}</span>
              <span
                aria-hidden
                key={laeuft ? `${schritt}:${runde}` : 'fest'}
                className={`absolute inset-y-0 left-0 rounded-full bg-marke ${laeuft ? 'segment-fuellt' : ''}`}
                style={laeuft ? { animationDuration: `${dauer}ms` } : { width: nr < schritt ? '100%' : 0 }}
              />
            </li>
          );
        })}
      </ol>
      <span aria-hidden className="shrink-0 text-[0.75rem] text-text-hell">{t('1 Min.', '1 min')}</span>
    </div>
  );
}

// ─── Jetzt bist du dran ─────────────────────────────────────────────────────

/** Hier traegt man in claude.ai einen Connector ein (seit 16.10.2026 aus config.ts). */
export { CLAUDE_CONNECTORS };

const Nummer = ({ n, an = false }: { n: number; an?: boolean }) => (
  <span aria-hidden className={`grid size-[1.375rem] place-items-center rounded-full text-[0.6875rem] font-bold ${
    an ? 'bg-knopf text-auf-marke' : 'border-[1.5px] border-marke text-marke-tief'}`}>{n}</span>
);

/**
 * Der letzte Bildschirm: verbinden, hier und jetzt (Entwurf „Verbinden C“,
 * Canvas „99takes Onboarding Politur“, gewaehlt von Robert am 12.10.2026).
 * Bis dahin stand hier ein Satz zum Kopieren, und der Weg zum Verbinden war
 * auf /runs ein kleiner Link „So geht es“. Verbinden ist aber der eine
 * Schritt, ohne den nichts weiter geht. Der Satz zum Start steht seitdem
 * schon bei den Fragen (Fragen.tsx) und danach in „Dein erster Run“.
 *
 * `freigabe`: wer aus claude.ai kommt (Ziel /connect), verbindet gerade
 * schon. Dem zeigen wir keine Anleitung, nur den Weg zur Freigabe.
 */
export function JetztDu({ titel, zeit = Infinity, guthaben, aktion, spaeter, freigabe = false }: {
  titel?: RefObject<HTMLHeadingElement | null>;
  zeit?: number;
  guthaben: number | null;
  aktion: ReactNode;
  /** „Später“ neben dem Knopf, nur am Desktop; am Telefon steht es in der Leiste. */
  spaeter?: ReactNode;
  freigabe?: boolean;
}) {
  const credits = guthaben != null && guthaben > 0 && (
    <Rein ab={freigabe ? 300 : 1100} zeit={zeit} className="mt-4">
      <p className="m-0 text-[0.84375rem] text-text-leise">
        <b className="font-semibold text-marke-tief tabular-nums">{new Intl.NumberFormat(locale()).format(guthaben)}</b>{t(' Credits liegen auf deinem Konto. Verbinden kostet nichts.', ' credits are in your account. Connecting is free.')}
      </p>
    </Rein>
  );
  return (
    <section>
      <div className="text-[0.75rem] font-semibold tracking-[0.07em] text-text-hell uppercase">{t('Jetzt du', 'Your turn')}</div>
      <h1 ref={titel} tabIndex={-1} className="m-0 mt-2 font-serif text-[1.6875rem] leading-[1.1] font-semibold tracking-[-0.018em] text-tinte outline-none sm:text-[2.25rem]">
        {freigabe ? t('Gleich bist du verbunden.', 'You are almost connected.') : t('Einmal verbinden, dann fragst du in Claude.', 'Connect once, then ask in Claude.')}
      </h1>
      {freigabe ? (
        <p className="m-0 mt-2 text-[0.9375rem] leading-relaxed text-text-leise sm:text-[1rem]">
          {t('Claude wartet auf deine Freigabe. Danach fragst du dort ganz normal.', 'Claude is waiting for your approval. After that you just ask there as usual.')}
        </p>
      ) : (
        <>
          <ol className="m-0 mt-4 list-none overflow-hidden rounded-[0.875rem] border border-linie bg-flaeche p-0 text-[0.875rem] leading-snug text-text shadow-karte">
            <li>
              <Rein ab={150} zeit={zeit} className="grid grid-cols-[1.375rem_minmax(0,1fr)] gap-2.5 border-b border-linie-zart px-3.5 py-3">
                <Nummer n={1} an />
                <div className="min-w-0">
                  <p className="m-0 mb-2">{t('Diese Adresse kopieren', 'Copy this address')}</p>
                  <Kopierzeile text={MCP_URL} />
                </div>
              </Rein>
            </li>
            <li>
              <Rein ab={450} zeit={zeit} className="grid grid-cols-[1.375rem_minmax(0,1fr)] gap-2.5 border-b border-linie-zart px-3.5 py-3">
                <Nummer n={2} />
                <div className="min-w-0">
                  <p className="m-0">{t(
                    <>In Claude unter <b className="font-semibold text-tinte">{connectorPfad()}</b> „Hinzufügen“ → „Benutzerdefinierten Connector hinzufügen“ wählen, die Adresse einfügen und mit deinem 99takes-Konto anmelden.</>,
                    <>In Claude, go to <b className="font-semibold text-tinte">{connectorPfad()}</b>, choose “Add” → “Add custom connector”, paste the address and sign in with your 99takes account.</>,
                  )}</p>
                  <p className="m-0 mt-1.5 text-[0.8125rem] leading-relaxed text-text-hell">{fensterHinweis()}</p>
                </div>
              </Rein>
            </li>
            <li>
              <Rein ab={750} zeit={zeit} className="grid grid-cols-[1.375rem_minmax(0,1fr)] gap-2.5 border-b border-linie-zart px-3.5 py-3">
                <Nummer n={3} />
                <p className="m-0">{freigabeHinweis()}</p>
              </Rein>
            </li>
            <li>
              <Rein ab={1050} zeit={zeit} className="grid grid-cols-[1.375rem_minmax(0,1fr)] gap-2.5 px-3.5 py-3">
                <Nummer n={4} />
                <p className="m-0">{t(
                  <>Zum Test in einem neuen Chat fragen: <span className="text-tinte">„{TESTSATZ}“</span></>,
                  <>To test, ask in a new chat: <span className="text-tinte">“{TESTSATZ_EN}”</span></>,
                )}</p>
              </Rein>
            </li>
          </ol>
          <p className="m-0 mt-3 text-[0.8125rem] text-text-leise">
            {t('Du nutzt Claude Code? ', 'Using Claude Code? ')}<a href="/account#verbinden" className="text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke">{t('Befehl fürs Terminal', 'Command for the terminal')}</a>
          </p>
        </>
      )}
      {credits}
      {/* Auf dem Telefon stehen beide Knoepfe in der festen Leiste des Rundgangs. */}
      <div className="mt-8 hidden items-center justify-end gap-3 sm:flex">{spaeter}{aktion}</div>
    </section>
  );
}

// ─── Woran du siehst, dass 99takes mitarbeitet (auf /guide) ─────────────────
//
// ANLASS (Tobias, 28.09.2026): „Wo sehe ich dann, dass ich mit 99takes
// arbeite? Das sichtbar zu machen waere cool." 99takes hat kein eigenes
// Fenster; man erkennt es an drei Stellen. Im Rundgang steht davon seit
// 29.09.2026 nur das Zeichen in der Werkzeugzeile (Szene 1), ausfuehrlich auf /guide#erkennen.
//
// Was hier ueber Claude steht, ist bewusst allgemein: wie claude.ai
// Werkzeugaufrufe genau darstellt, ist nicht belegt (Wurzel-AGENTS.md,
// „Welcher Zugangsweg heute funktioniert") und aendert sich mit Claude.
// Sicher ist: jeder Aufruf erscheint mit dem Titel aus anzeige.ts, Claude Code
// zeigt mit /mcp, ob der Server verbunden ist, und get_balance antwortet mit
// dem Guthaben dieses Kontos (seit 28.09.2026 soll Claude das auch von sich
// aus sagen, mcp.ts „SICHTBAR MACHEN").

export const ERKENNEN_UNTER = '99takes hat kein eigenes Fenster. Du arbeitest in Claude, und 99takes antwortet darin mit. An drei Stellen siehst du, dass es verbunden ist und arbeitet.';
export const ERKENNEN_UNTER_EN = '99takes has no window of its own. You work in Claude, and 99takes answers inside it. In three places you can see that it is connected and working.';

export function Erkennen() {
  return (
    <>
      <ol className="m-0 grid list-none gap-3 p-0 lg:grid-cols-3">
        <li className="flex flex-col rounded-xl border border-linie bg-flaeche p-4 shadow-flaeche">
          <span className="font-mono text-[0.75rem] font-semibold text-marke-tief">01</span>
          <h2 className="m-0 mt-1.5 text-[1rem] font-semibold text-tinte">{t('Der Test-Satz', 'The test sentence')}</h2>
          <p className="m-0 mt-1.5 mb-3 text-[0.84375rem] leading-relaxed text-text-leise">{t('Tipp das in Claude:', 'Type this in Claude:')}</p>
          <Kopierzeile art="text" text={testsatz()} />
          <p className="m-0 mt-3 text-[0.84375rem] leading-relaxed text-text-leise">
            {t(<>
              Nennt Claude dein Guthaben in Credits, kommt die Zahl aus deinem 99takes-Konto: es ist verbunden.
              Antwortet Claude allgemein und ohne Zahl, ist 99takes in diesem Chat nicht aktiv.
            </>, <>
              If Claude names your balance in credits, the number comes from your 99takes account: it is connected.
              If Claude answers in general terms without a number, 99takes is not active in this chat.
            </>)}
          </p>
        </li>
        <li className="flex flex-col rounded-xl border border-linie bg-flaeche p-4 shadow-flaeche">
          <span className="font-mono text-[0.75rem] font-semibold text-marke-tief">02</span>
          <h2 className="m-0 mt-1.5 text-[1rem] font-semibold text-tinte">In Claude</h2>
          <p className="m-0 mt-1.5 mb-3 text-[0.84375rem] leading-relaxed text-text-leise">
            {t('Jeder Schritt von 99takes erscheint als eigene Zeile mit seinem Namen, etwa so:', 'Each 99takes step shows up as its own line with its name, like this:')}
          </p>
          <div aria-hidden className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border border-linie bg-ebene px-3 py-2 text-[0.78125rem] text-text-leise">
            <Zeichen klein className="size-4" />
            <b className="font-semibold text-tinte">{ANZEIGE.get_balance.title}</b>
            <code className="font-mono text-[0.71875rem] text-text-hell">get_balance</code>
            <span className="ml-auto text-gut">{t('kostenlos', 'free')}</span>
          </div>
          <p className="m-0 mt-3 text-[0.84375rem] leading-relaxed text-text-leise">
            {t(<>
              Claude sagt dazu, wenn eine Antwort von 99takes kommt, und nennt vor jedem Schritt mit Credits den Preis.
              Aussehen und Wortlaut hängen von deiner Claude-Version ab.
            </>, <>
              Claude says when an answer comes from 99takes, and names the price before every step that costs credits.
              Look and wording depend on your version of Claude.
            </>)}
          </p>
          {/* Anlass 29.09.2026: beim Speichern der Zielgruppe zeigte claude.ai
              „Claude möchte Zielgruppe speichern verwenden" samt Eingabe als Code.
              Die Abfrage kommt von claude.ai, nicht von 99takes. */}
          <p className="m-0 mt-2 text-[0.84375rem] leading-relaxed text-text-leise">
            {t(<>
              Standardmäßig fragt claude.ai bei jedem Werkzeug von 99takes einzeln, ob es das darf, und zeigt dazu die
              Eingabe als Code. Unter <em>{connectorPfad()} → 99takes → Tool-Berechtigungen</em> stellst du beide
              Gruppen auf „Immer erlauben“, dann fragt es nicht mehr. Den Preis nennt Claude trotzdem vorher, eine neue
              Zielgruppe kostet einmalig {EINRICHTUNG} Credits.
            </>, <>
              By default claude.ai asks for every 99takes tool separately whether it may, and shows the input as code.
              Under <em>{connectorPfad()} → 99takes → Tool permissions</em>, set both groups to “Always allow” and it
              stops asking. Claude still names the price first; a new audience costs {EINRICHTUNG} credits once.
            </>)}
          </p>
        </li>
        <li className="flex flex-col rounded-xl border border-linie bg-flaeche p-4 shadow-flaeche">
          <span className="font-mono text-[0.75rem] font-semibold text-marke-tief">03</span>
          <h2 className="m-0 mt-1.5 text-[1rem] font-semibold text-tinte">{t('Hier im Konto', 'Here in your account')}</h2>
          <p className="m-0 mt-1.5 text-[0.84375rem] leading-relaxed text-text-leise">
            {t(<>
              Nach dem ersten Aufruf aus Claude ist „Mit Claude verbinden“ abgehakt. Jeder Run steht danach unter
              „Runs“, mit Urteil, Kosten und dem, was die Personas gesagt haben, und dein Guthaben sinkt um den
              genannten Preis.
            </>, <>
              After the first call from Claude, “Connect to Claude” is checked off. Every run then appears under
              “Runs”, with the verdict, the cost and what the personas said, and your balance drops by the stated
              price.
            </>)}
          </p>
        </li>
      </ol>

      <div className="mt-4 rounded-xl border border-linie bg-ebene p-4">
        <h2 className="m-0 text-[0.8125rem] font-semibold tracking-[0.06em] text-text-hell uppercase">{t('Wenn nichts passiert', 'If nothing happens')}</h2>
        <ul className="m-0 mt-2 list-disc space-y-1.5 pl-5 text-[0.875rem] leading-relaxed text-text-leise">
          {t(<>
          <li><b className="font-semibold text-tinte">Gerade verbunden:</b> Öffne einen neuen Chat. Ein Chat, der schon offen war, kennt die Werkzeuge von 99takes noch nicht.</li>
          <li><b className="font-semibold text-tinte">Neue Funktion fehlt:</b> Claude merkt sich die Werkzeuge eines Connectors. Nach einem Update von 99takes aktualisierst du die Werkzeugliste einmal (claude.ai: <em>{connectorPfad()} → 99takes</em>; Claude Code: <code className="font-mono text-[0.8125rem]">/mcp</code>, 99takes, neu verbinden) und öffnest dann einen neuen Chat.</li>
          <li><b className="font-semibold text-tinte">claude.ai:</b> Unter <em>{connectorPfad()}</em> muss 99takes stehen. Connectors lassen sich pro Chat abschalten; ist es im Chat aus, antwortet Claude ohne 99takes.</li>
          <li><b className="font-semibold text-tinte">Claude Code:</b> <code className="font-mono text-[0.8125rem]">/mcp</code> eintippen. Dort muss 99takes als verbunden stehen.</li>
          <li><b className="font-semibold text-tinte">Immer:</b> „mit 99takes“ in den Satz nehmen, etwa „Prüf das mit 99takes“. Dann greift Claude gezielt darauf zu. Dauerhaft geht es über die persönlichen Einstellungen in claude.ai, etwa mit dem Satz „Für Werbe- und Website-Texte immer 99takes verwenden“.</li>
          </>, <>
          <li><b className="font-semibold text-tinte">Just connected:</b> Open a new chat. A chat that was already open does not know the 99takes tools yet.</li>
          <li><b className="font-semibold text-tinte">New feature missing:</b> Claude remembers a connector's tools. After a 99takes update, refresh the tool list once (claude.ai: <em>{connectorPfad()} → 99takes</em>; Claude Code: <code className="font-mono text-[0.8125rem]">/mcp</code>, 99takes, reconnect) and then open a new chat.</li>
          <li><b className="font-semibold text-tinte">claude.ai:</b> 99takes must be listed under <em>{connectorPfad()}</em>. Connectors can be turned off per chat. If it is off in the chat, Claude answers without 99takes.</li>
          <li><b className="font-semibold text-tinte">Claude Code:</b> Type <code className="font-mono text-[0.8125rem]">/mcp</code>. 99takes must show as connected there.</li>
          <li><b className="font-semibold text-tinte">Always:</b> Put “with 99takes” in the sentence, for example “Check this with 99takes”. Then Claude uses it on purpose. To make it permanent, use your personal preferences in claude.ai, for example with the sentence “Always use 99takes for ad and website copy”.</li>
          </>)}
        </ul>
      </div>
    </>
  );
}
